import { useState, memo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { ArrowLeft, ZoomOut, Star, Landmark, MapPin } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const officeStates = ["New York", "Pennsylvania", "New Jersey", "Delaware", "Maryland", "Florida"];
const licensureOnlyStates = ["Ohio", "District of Columbia"];

const stateLabelCoords: Record<string, { coords: [number, number]; abbr: string }> = {
  "New York": { coords: [-75.5, 43.0], abbr: "NY" },
  "Pennsylvania": { coords: [-77.5, 41.0], abbr: "PA" },
  "New Jersey": { coords: [-74.4, 40.1], abbr: "NJ" },
  "Delaware": { coords: [-75.5, 39.0], abbr: "DE" },
  "Maryland": { coords: [-76.7, 39.05], abbr: "MD" },
  "Florida": { coords: [-81.8, 28.5], abbr: "FL" },
  "Ohio": { coords: [-82.7, 40.4], abbr: "OH" },
  "District of Columbia": { coords: [-77.0, 38.9], abbr: "D.C." },
};

interface OfficeMarker {
  city: string;
  state: string;
  phone: string;
  address: string;
  coords: [number, number];
}

const officeMarkers: OfficeMarker[] = [
  { city: "Albany", state: "NY", phone: "(838) 221-3600", address: "12 Corporate Woods Blvd, Suite 410", coords: [-73.7562, 42.6526] },
  { city: "Syracuse", state: "NY", phone: "(680) 697-8860", address: "126 North Salina St, Suite 210", coords: [-76.1474, 43.0481] },
  { city: "Buffalo", state: "NY", phone: "(680) 697-8860", address: "500 Corporate Pkwy, Suite 108", coords: [-78.8784, 42.8864] },
  { city: "New York", state: "NY", phone: "(212) 377-5229", address: "229 West 36th St, 8th Floor", coords: [-74.006, 40.7128] },
  { city: "Philadelphia", state: "PA", phone: "(215) 569-2400", address: "Two Logan Square, Suite 700", coords: [-75.1652, 39.9526] },
  { city: "Harrisburg", state: "PA", phone: "(717) 980-3700", address: "120 State Street", coords: [-76.8844, 40.2732] },
  { city: "Pittsburgh", state: "PA", phone: "(412) 392-7510", address: "301 Grant St, Suite 270", coords: [-79.9959, 40.4406] },
  { city: "Marlton", state: "NJ", phone: "(856) 316-0511", address: "5 Greentree Centre, Suite 310", coords: [-74.9218, 39.8912] },
  { city: "New Brunswick", state: "NJ", phone: "(732) 884-2300", address: "317 George St, 3rd Floor", coords: [-74.4518, 40.4862] },
  { city: "Atlantic City", state: "NJ", phone: "(609) 383-2300", address: "1008 South New Road", coords: [-74.4229, 39.3643] },
  { city: "Wilmington", state: "DE", phone: "(302) 493-0569", address: "1523 Concord Pike, Suite 200", coords: [-75.5398, 39.7391] },
  { city: "Baltimore", state: "MD", phone: "(443) 759-3827", address: "100 International Dr, 23rd Floor", coords: [-76.6122, 39.2904] },
  { city: "Tampa", state: "FL", phone: "(561) 624-1051", address: "100 S. Ashley Dr, Suite 600", coords: [-82.4572, 27.9506] },
  { city: "Sarasota", state: "FL", phone: "(941) 328-6200", address: "5589 Marquesas Circle, Suite 101", coords: [-82.5308, 27.3364] },
  { city: "Palm Beach Gardens", state: "FL", phone: "(561) 624-1051", address: "11631 Kew Gardens Ave, Suite 200", coords: [-80.1009, 26.8234] },
];

const zoomConfigs: Record<string, { center: [number, number]; zoom: number }> = {
  Albany: { center: [-73.8, 42.7], zoom: 8 },
  Syracuse: { center: [-76.2, 43.0], zoom: 7 },
  Buffalo: { center: [-78.9, 42.95], zoom: 8 },
  "New York": { center: [-74.0, 40.72], zoom: 10 },
  Philadelphia: { center: [-75.2, 39.98], zoom: 10 },
  Harrisburg: { center: [-76.8, 40.1], zoom: 7 },
  Pittsburgh: { center: [-80.0, 40.44], zoom: 10 },
  Marlton: { center: [-74.8, 39.95], zoom: 9 },
  "New Brunswick": { center: [-74.5, 40.4], zoom: 9 },
  "Atlantic City": { center: [-74.5, 39.35], zoom: 9 },
  Wilmington: { center: [-75.5, 39.5], zoom: 7 },
  Baltimore: { center: [-76.5, 39.2], zoom: 8 },
  Tampa: { center: [-82.5, 28.5], zoom: 5 },
  Sarasota: { center: [-82.2, 27.1], zoom: 7 },
  "Palm Beach Gardens": { center: [-80.3, 26.3], zoom: 6 },
};

const OfficeLocationsMap = memo(({ onPinClick }: { onPinClick?: (locationKey: string) => void }) => {
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedOffice, setSelectedOffice] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-77, 37.5]);
  const [mapZoom, setMapZoom] = useState(1);

  const handlePinClick = useCallback((marker: OfficeMarker) => {
    const config = zoomConfigs[marker.city];
    if (config) {
      setSelectedOffice(marker.city);
      setMapCenter(config.center);
      setMapZoom(config.zoom);
      setHoveredCity(null);
      onPinClick?.(marker.city);
    }
  }, [onPinClick]);

  const handleBackToOverview = useCallback(() => {
    setSelectedOffice(null);
    setMapCenter([-77, 37.5]);
    setMapZoom(1);
    setHoveredCity(null);
  }, []);

  const activeOffice = selectedOffice ? officeMarkers.find((m) => m.city === selectedOffice) : null;

  return (
    <div className="w-full bg-card border border-border rounded-sm overflow-hidden">
      <div className="p-6 md:p-10 pb-0 md:pb-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1" />
          <h3 className="font-heading text-2xl md:text-3xl text-foreground text-center flex-1">
            {selectedOffice ? `${selectedOffice} Office` : "Our Office Locations"}
          </h3>
          <div className="flex-1" />
        </div>
        <p className="text-xs tracking-[0.15em] uppercase font-body text-muted-foreground text-center mb-4">
          {selectedOffice && activeOffice
            ? `${activeOffice.address} • ${activeOffice.phone}`
            : "15 offices across 6 states • Licensed in 8 jurisdictions — click a pin to explore"}
        </p>
      </div>

      <div
        className="relative w-full"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
      >
        {/* Prominent zoom-out overlay button */}
        <AnimatePresence>
          {selectedOffice && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              onClick={handleBackToOverview}
              className="absolute top-3 left-3 z-30 inline-flex items-center gap-2 px-4 py-2.5 bg-background/95 backdrop-blur-sm border border-border rounded-sm shadow-lg text-xs tracking-[0.15em] uppercase font-body text-foreground hover:text-primary hover:border-primary transition-colors cursor-pointer"
            >
              <ZoomOut className="w-4 h-4" />
              <span>Zoom Out</span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Hovered city tooltip — follows cursor */}
        <AnimatePresence>
          {hoveredCity && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.08 }}
              className="absolute z-20 pointer-events-none bg-popover border border-border shadow-lg px-4 py-3 rounded-sm"
              style={{
                left: tooltipPos.x + 16,
                top: tooltipPos.y - 16,
                transform: "translateY(-100%)",
              }}
            >
              {(() => {
                const m = officeMarkers.find((o) => o.city === hoveredCity);
                if (!m) return null;
                return (
                  <>
                    <p className="font-heading text-base text-foreground whitespace-nowrap">
                      {m.city}, {m.state}
                    </p>
                    <p className="text-xs font-body text-muted-foreground mt-1">{m.address}</p>
                    <a href={`tel:${m.phone.replace(/\D/g, '')}`} className="text-xs font-body text-muted-foreground hover:text-primary transition-colors mt-0.5">{m.phone}</a>
                    <p className="text-[10px] font-body text-primary mt-1.5">Click to explore</p>
                  </>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 1400,
            center: [-77, 37.5],
          }}
          width={700}
          height={750}
          style={{ width: "100%", height: "auto" }}
        >
          <ZoomableGroup
            center={mapCenter}
            zoom={mapZoom}
            minZoom={0.8}
            maxZoom={12}
            translateExtent={[[0, -100], [700, 800]]}
            onMoveEnd={() => {}}
            filterZoomEvent={(evt: any) => {
              // Block all wheel/scroll zoom — let the page scroll normally
              if (evt?.type === "wheel" || evt?.type === "scroll") return false;
              // Block pinch-to-zoom touch events to prevent accidental zoom
              if (evt?.type === "touchmove" || evt?.type === "touchstart") return false;
              return true;
            }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;
                  const isOffice = officeStates.includes(stateName);
                  const isLicensure = licensureOnlyStates.includes(stateName);
                  const fillColor = isOffice
                    ? "hsl(40, 56%, 54%)"
                    : isLicensure
                    ? "hsl(40, 35%, 70%)"
                    : "hsl(40, 20%, 85%)";
                  const hoverColor = isOffice
                    ? "hsl(40, 56%, 62%)"
                    : isLicensure
                    ? "hsl(40, 35%, 76%)"
                    : "hsl(40, 20%, 78%)";
                  return (
                    <Geography
                      key={geo.rpiKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="hsl(0, 0%, 4%)"
                      strokeWidth={0.5 / mapZoom}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        default: { outline: "none", cursor: "default" },
                        hover: {
                          fill: hoverColor,
                          outline: "none",
                          cursor: "default",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* State name labels */}
            {!selectedOffice && Object.entries(stateLabelCoords).map(([stateName, { coords, abbr }]) => {
              const isOffice = officeStates.includes(stateName);
              return (
                <Marker key={`label-${stateName}`} coordinates={coords}>
                  <g className="pointer-events-none">
                    <text
                      textAnchor="middle"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: `${Math.max(4, 7 / mapZoom)}px`,
                        fill: "none",
                        stroke: "hsl(0, 0%, 4%)",
                        strokeWidth: `${2.5 / mapZoom}px`,
                        strokeLinejoin: "round",
                        fontWeight: 700,
                        letterSpacing: "1px",
                      }}
                    >
                      {abbr}
                    </text>
                    <text
                      textAnchor="middle"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: `${Math.max(4, 7 / mapZoom)}px`,
                        fill: isOffice ? "hsl(0, 0%, 100%)" : "hsl(40, 35%, 45%)",
                        fontWeight: 700,
                        letterSpacing: "1px",
                      }}
                    >
                      {abbr}
                    </text>
                  </g>
                </Marker>
              );
            })}

            {officeMarkers.map((marker) => {
              const isSelected = selectedOffice === marker.city;
              const iconScale = isSelected ? 1.4 / mapZoom : 1.0 / mapZoom;
              const hitAreaSize = Math.max(12 / mapZoom, 6);
              return (
                <Marker
                  key={`${marker.city}-${marker.state}`}
                  coordinates={marker.coords}
                >
                  {isSelected && (
                    <circle
                      r={14 / mapZoom}
                      fill="none"
                      stroke="hsl(40, 56%, 54%)"
                      strokeWidth={1.5 / mapZoom}
                      opacity={0.5}
                    />
                  )}
                  <circle
                    r={hitAreaSize}
                    fill="transparent"
                    className="cursor-pointer"
                    style={{ zIndex: 10 }}
                    onClick={(e) => { e.stopPropagation(); handlePinClick(marker); }}
                    onMouseEnter={() => setHoveredCity(marker.city)}
                    onMouseLeave={() => setHoveredCity(null)}
                  />
                  <g
                    transform={`scale(${iconScale}) translate(-12, -12)`}
                    className="pointer-events-none"
                  >
                    <circle cx="12" cy="12" r="11" fill={isSelected ? "hsl(0, 0%, 100%)" : "hsl(0, 0%, 4%)"} stroke={isSelected ? "hsl(0, 0%, 4%)" : "hsl(40, 56%, 54%)"} strokeWidth="1.5" />
                    <g transform="translate(4, 3) scale(0.667)" fill="none" stroke={isSelected ? "hsl(0, 0%, 4%)" : "hsl(40, 56%, 54%)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="3" x2="12" y2="21" />
                      <line x1="4" y1="7" x2="20" y2="7" />
                      <path d="M4 7l2 6a4 4 0 0 0 4 0l2-6" />
                      <path d="M12 7l2 6a4 4 0 0 0 4 0l2-6" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                    </g>
                  </g>
                  {isSelected && (
                    <>
                      <text
                        textAnchor="middle"
                        y={-12 / mapZoom}
                        style={{
                          fontFamily: "Montserrat, sans-serif",
                          fontSize: `${Math.max(3, 6 / mapZoom)}px`,
                          fill: "none",
                          stroke: "hsl(0, 0%, 4%)",
                          strokeWidth: `${3 / mapZoom}px`,
                          strokeLinejoin: "round",
                          fontWeight: 700,
                          letterSpacing: "1px",
                          textTransform: "uppercase" as const,
                        }}
                      >
                        {marker.city}, {marker.state}
                      </text>
                      <text
                        textAnchor="middle"
                        y={-12 / mapZoom}
                        style={{
                          fontFamily: "Montserrat, sans-serif",
                          fontSize: `${Math.max(3, 6 / mapZoom)}px`,
                          fill: "hsl(0, 0%, 100%)",
                          fontWeight: 700,
                          letterSpacing: "1px",
                          textTransform: "uppercase" as const,
                        }}
                      >
                        {marker.city}, {marker.state}
                      </text>
                    </>
                  )}
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 flex-wrap px-6 pb-6">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="hsl(40, 56%, 54%)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="11" fill="hsl(0, 0%, 4%)" />
            <g transform="translate(4, 3) scale(0.667)">
              <line x1="12" y1="3" x2="12" y2="21" />
              <line x1="4" y1="7" x2="20" y2="7" />
              <path d="M4 7l2 6a4 4 0 0 0 4 0l2-6" />
              <path d="M12 7l2 6a4 4 0 0 0 4 0l2-6" />
              <line x1="8" y1="21" x2="16" y2="21" />
            </g>
          </svg>
          <span className="text-xs font-body text-muted-foreground tracking-wide uppercase">
            Office Location
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-4 rounded-sm" style={{ backgroundColor: "hsl(40, 56%, 54%)" }} />
          <span className="text-xs font-body text-muted-foreground tracking-wide uppercase">
            Office State
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-4 rounded-sm" style={{ backgroundColor: "hsl(40, 35%, 70%)" }} />
          <span className="text-xs font-body text-muted-foreground tracking-wide uppercase">
            Licensed State (OH, D.C.)
          </span>
        </div>
      </div>
    </div>
  );
});

OfficeLocationsMap.displayName = "OfficeLocationsMap";

export default OfficeLocationsMap;
